import { DetailPageTopBar } from '@app/DetailPageTopBar';
import { EDetailPageTopBarItemType } from '@app/detailPageTopBar.model';
import {
  Button,
  ComponentType,
  ContentLoader,
  ErrorOption,
  Form,
  IconName,
  RecordsNotFound,
  useForm,
} from '@gcl/react-core';
import React, { useState } from 'react';
import { useProjectId } from '@config/config.hooks';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { useQueryClient } from '@tanstack/react-query';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { useMutationWithOnSuccessOnError } from '@common/useMutationWithOnSuccessOnError.hooks';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { DiscussionFormKeys } from './DiscussionList.models';
import './DiscussionList.scss';
import { DownloadButton } from '@file/DownloadButton';
import { downloadTransmittalProjectDiscussionsPdfAsync } from '@transmittal/transmittal.services';
import { IFileResponse, IPdfFileDownloadRequest } from '@file/projectFile/projectFile.models';
import { downloadLoadingRemovalCertProjectDiscussionsPdfAsync } from '@permitToLoadRemove/permitToLoadRemove.services';
import { downloadDesignBriefProjectDiscussionsPdfAsync } from '@designBrief/designBrief.services';
import {
  downloadDesignModificationCheckerProjectDiscussionsPdfAsync,
  downloadDesignModificationDesignerProjectDiscussionsPdfAsync,
} from '@designModification/designModification.services';
import { useDeferLoading } from '@common/loading.hooks';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import {
  useCreateProjectDiscussion,
  useDeleteProjectDiscussion,
  useFetchProjectDiscussionList,
  useUpdateProjectDiscussionAsDraft,
  useUpdateProjectDiscussionAsIssue,
  useUpdateProjectDiscussionReplyAsDraft,
  useUpdateProjectDiscussionReplyAsIssue,
  useUpdateProjectDiscussionRepliesAsDraft,
  useUpdateProjectDiscussionRepliesAsIssue,
  useUpdateProjectDiscussionsAsDraft,
  useUpdateProjectDiscussionsAsIssue,
} from './projectDiscussion/projectDiscussion.queries';
import {
  IProjectDiscussion,
  IProjectDiscussionFormProps,
  IReplyProjectDiscussionRequest,
  IUpdateProjectDiscussionRequest,
} from './projectDiscussion/projectDiscussion.models';
import { DiscussionRow } from './discussionRow/DiscussionRow';
import { EProjectDiscussionFormType, EProjectDiscussionStatus } from './projectDiscussion/projectDiscussion.constants';
import { EProjectDiscussionFileType } from './projectDiscussion/projectDiscussionFile/projectDiscussionFile.constants';

interface IDiscussionListProps extends IProjectDiscussionFormProps {
  fileFormType: EProjectFileType;
  discussionFileType: EProjectDiscussionFileType;
  issueToTypes?: string[];
  canComment?: boolean;
  canResponse?: boolean | ((discussion: IProjectDiscussion) => boolean);
  isReadOnly?: boolean;
  defaultType?: string;
  canViewDraftComment: boolean;
  showNoneWhenNoRecordInReadOnly?: boolean;
  title?: string;
}

export const DiscussionList = ({
  fileFormType,
  discussionFileType,
  issueToTypes = [],
  canComment = false,
  canResponse = false,
  isReadOnly = false,
  defaultType,
  canViewDraftComment,
  showNoneWhenNoRecordInReadOnly = false,
  title,
  ...projectDiscussionFormProps
}: IDiscussionListProps) => {
  const {
    formType,
    projectTransmittalId,
    projectDesignBriefId,
    projectDesignModificationId,
    projectLoadingRemovalCertId,
    projectLambethReviewerResponseId,
  } = projectDiscussionFormProps;
  const queryClient = useQueryClient();
  const [uploadStatusMap, setUploadStatusMap] = React.useState<{ [key: string]: boolean }>({});
  const isUploading = React.useMemo(
    () => Object.values(uploadStatusMap).some((status) => status === true),
    [JSON.stringify(uploadStatusMap)],
  );
  const localCanComment = React.useMemo(() => {
    if (isReadOnly) return !isReadOnly;
    return canComment;
  }, [canComment, isReadOnly]);
  const localCanResponse = (discussion: IProjectDiscussion) => {
    if (isReadOnly) return !isReadOnly;

    return (
      canResponse &&
      (typeof canResponse === 'boolean' || (typeof canResponse === 'function' && canResponse(discussion)))
    );
  };
  const [selectedDiscussions, setSelectedDiscussions] = useState<number[]>([]);
  const [selectedReplies, setSelectedReplies] = useState<number[]>([]);
  const selectedCount = React.useMemo(() => {
    return (selectedDiscussions.length || 0) + (selectedReplies.length || 0);
  }, [JSON.stringify(selectedDiscussions), JSON.stringify(selectedReplies)]);
  const projectId = useProjectId();
  const [selectedIssueToType, setSelectedIssueToType] = React.useState<string | undefined>(defaultType);

  const {
    isLoading: isLoadingDiscussions,
    isFetching: isFetchingDiscussions,
    data: discussions,
    error: errorDiscussions,
  } = useFetchProjectDiscussionList({ projectId, ...projectDiscussionFormProps });

  const canPrint = React.useMemo(() => {
    return discussions && discussions.length > 0;
  }, [JSON.stringify(discussions)]);

  const {
    mutate: mutateCreateProjectDiscussion,
    error: errorCreateProjectDiscussion,
    isSuccess: isSuccessCreateProjectDiscussion,
    isError: isErrorCreateProjectDiscussion,
    isLoading: isLoadingCreateProjectDiscussion,
    reset: resetCreateProjectDiscussion,
  } = useCreateProjectDiscussion();

  const {
    mutate: mutateUpdateProjectDiscussionsAsDraft,
    error: errorUpdateProjectDiscussionsAsDraft,
    isSuccess: isSuccessUpdateProjectDiscussionsAsDraft,
    isError: isErrorUpdateProjectDiscussionsAsDraft,
    isLoading: isLoadingUpdateProjectDiscussionsAsDraft,
    reset: resetUpdateProjectDiscussionsAsDraft,
  } = useUpdateProjectDiscussionsAsDraft();

  const {
    mutate: mutateUpdateProjectDiscussionsAsIssue,
    error: errorUpdateProjectDiscussionsAsIssue,
    isSuccess: isSuccessUpdateProjectDiscussionsAsIssue,
    isError: isErrorUpdateProjectDiscussionsAsIssue,
    isLoading: isLoadingUpdateProjectDiscussionsAsIssue,
    reset: resetUpdateProjectDiscussionsAsIssue,
  } = useMutationWithOnSuccessOnError(useUpdateProjectDiscussionsAsIssue, () => {
    setSelectedDiscussions([]);
  });

  const {
    mutate: mutateUpdateProjectDiscussionAsDraft,
    error: errorUpdateProjectDiscussionAsDraft,
    isSuccess: isSuccessUpdateProjectDiscussionAsDraft,
    isError: isErrorUpdateProjectDiscussionAsDraft,
    isLoading: isLoadingUpdateProjectDiscussionAsDraft,
    reset: resetUpdateProjectDiscussionAsDraft,
  } = useUpdateProjectDiscussionAsDraft();

  const {
    mutate: mutateUpdateProjectDiscussionAsIssue,
    error: errorUpdateProjectDiscussionAsIssue,
    isSuccess: isSuccessUpdateProjectDiscussionAsIssue,
    isError: isErrorUpdateProjectDiscussionAsIssue,
    isLoading: isLoadingUpdateProjectDiscussionAsIssue,
    reset: resetUpdateProjectDiscussionAsIssue,
  } = useMutationWithOnSuccessOnError(useUpdateProjectDiscussionAsIssue, (variables: any) => {
    onSelectDiscussion(Number(variables.id), false);
  });

  const onGenerateUpdateDiscussionFn = (discussionId: number, issueToType: string | undefined, isDraft: boolean) => {
    return (data: any) => {
      const newDiscussions = (data[DiscussionFormKeys.FORM] as any[]) || [];
      if (!newDiscussions[discussionId]) return;
      const discussionRequest = convertFormValueToUpdateRequest(newDiscussions[discussionId]);
      if (isDraft) mutateUpdateProjectDiscussionAsDraft(discussionRequest);
      else mutateUpdateProjectDiscussionAsIssue({ ...discussionRequest, type: issueToType });
    };
  };

  const {
    mutate: mutateUpdateProjectDiscussionRepliesAsDraft,
    error: errorUpdateProjectDiscussionRepliesAsDraft,
    isSuccess: isSuccessUpdateProjectDiscussionRepliesAsDraft,
    isError: isErrorUpdateProjectDiscussionRepliesAsDraft,
    isLoading: isLoadingUpdateProjectDiscussionRepliesAsDraft,
    reset: resetUpdateProjectDiscussionRepliesAsDraft,
  } = useUpdateProjectDiscussionRepliesAsDraft();

  const {
    mutate: mutateUpdateProjectDiscussionRepliesAsIssue,
    error: errorUpdateProjectDiscussionRepliesAsIssue,
    isSuccess: isSuccessUpdateProjectDiscussionRepliesAsIssue,
    isError: isErrorUpdateProjectDiscussionRepliesAsIssue,
    isLoading: isLoadingUpdateProjectDiscussionRepliesAsIssue,
    reset: resetUpdateProjectDiscussionRepliesAsIssue,
  } = useMutationWithOnSuccessOnError(useUpdateProjectDiscussionRepliesAsIssue, () => {
    setSelectedReplies([]);
  });

  const {
    mutate: mutateUpdateProjectDiscussionReplyAsDraft,
    error: errorUpdateProjectDiscussionReplyAsDraft,
    isSuccess: isSuccessUpdateProjectDiscussionReplyAsDraft,
    isError: isErrorUpdateProjectDiscussionReplyAsDraft,
    isLoading: isLoadingUpdateProjectDiscussionReplyAsDraft,
    reset: resetUpdateProjectDiscussionReplyAsDraft,
  } = useUpdateProjectDiscussionReplyAsDraft();

  const {
    mutate: mutateUpdateProjectDiscussionReplyAsIssue,
    error: errorUpdateProjectDiscussionReplyAsIssue,
    isSuccess: isSuccessUpdateProjectDiscussionReplyAsIssue,
    isError: isErrorUpdateProjectDiscussionReplyAsIssue,
    isLoading: isLoadingUpdateProjectDiscussionReplyAsIssue,
    reset: resetUpdateProjectDiscussionReplyAsIssue,
  } = useMutationWithOnSuccessOnError(useUpdateProjectDiscussionReplyAsIssue, (variables: any) => {
    onSelectReply(Number(variables.id), false);
  });

  const { loading: isAnyActionLoading, setLoading: setIsAnyActionLoading } = useDeferLoading(
    isLoadingCreateProjectDiscussion ||
      isLoadingUpdateProjectDiscussionsAsDraft ||
      isLoadingUpdateProjectDiscussionsAsIssue ||
      isLoadingUpdateProjectDiscussionAsDraft ||
      isLoadingUpdateProjectDiscussionAsIssue ||
      isLoadingUpdateProjectDiscussionRepliesAsDraft ||
      isLoadingUpdateProjectDiscussionRepliesAsIssue ||
      isLoadingUpdateProjectDiscussionReplyAsDraft ||
      isLoadingUpdateProjectDiscussionReplyAsIssue,
  );

  const onGenerateUpdateReplyFn = (discussionId: number, isDraft: boolean) => {
    return (data: any) => {
      const newReplies = (data[DiscussionFormKeys.FORM] as any[]) || [];
      if (!newReplies[discussionId]) return;
      const discussionRequest = convertFormValueToReplyRequest(newReplies[discussionId]);
      if (isDraft) mutateUpdateProjectDiscussionReplyAsDraft(discussionRequest);
      else mutateUpdateProjectDiscussionReplyAsIssue(discussionRequest);
    };
  };

  const {
    mutate: mutateDeleteProjectDiscussion,
    error: errorDeleteProjectDiscussion,
    isSuccess: isSuccessDeleteProjectDiscussion,
    isError: isErrorDeleteProjectDiscussion,
    isLoading: isLoadingDeleteProjectDiscussion,
    reset: resetDeleteProjectDiscussion,
  } = useDeleteProjectDiscussion();

  const onDeleteDiscussion = (id: number) => {
    mutateDeleteProjectDiscussion({ projectId, id, formType: projectDiscussionFormProps.formType });
  };

  const onSelectDiscussion = (id: number, isSelected: boolean) => {
    setSelectedDiscussions((old) => {
      if (isSelected && !old.includes(id)) return [...old, id];
      return old.filter((n) => n !== id);
    });
  };

  const onSelectReply = (id: number, isSelected: boolean) => {
    setSelectedReplies((old) => {
      if (isSelected && !old.includes(id)) return [...old, id];
      return old.filter((n) => n !== id);
    });
  };

  const convertFormValueToUpdateRequest = (formValue: any) => {
    return {
      id: formValue[DiscussionFormKeys.ID],
      projectId,
      ...projectDiscussionFormProps,
      comment: formValue[DiscussionFormKeys.COMMENT],
      type: selectedIssueToType,
      rowVersion: formValue[DiscussionFormKeys.ROW_VERSION],
    } as IUpdateProjectDiscussionRequest;
  };

  const convertFormValueToReplyRequest = (formValue: any) => {
    return {
      id: formValue[DiscussionFormKeys.ID],
      projectId,
      ...projectDiscussionFormProps,
      furtherAction: formValue[DiscussionFormKeys.FURTHER_ACTION],
      rowVersion: formValue[DiscussionFormKeys.ROW_VERSION],
    } as IReplyProjectDiscussionRequest;
  };

  const onSaveAllDrafts = (data: any) => {
    const newItems = (data[DiscussionFormKeys.FORM] as any) || {};
    const discussionRequests = localCanComment
      ? Object.values<any>(newItems)
          .filter(
            (newDiscussion) =>
              newDiscussion[DiscussionFormKeys.STATUS] === EProjectDiscussionStatus.DRAFT_COMMENT &&
              newDiscussion[DiscussionFormKeys.COMMENT] &&
              newDiscussion[DiscussionFormKeys.COMMENT].trim() !== '',
          )
          .map((newDiscussion) => convertFormValueToUpdateRequest(newDiscussion))
      : [];

    const replyRequests = Object.values<any>(newItems)
      .filter(
        (newReply) =>
          localCanResponse(newReply) &&
          (newReply[DiscussionFormKeys.STATUS] === EProjectDiscussionStatus.COMMENTED ||
            newReply[DiscussionFormKeys.STATUS] === EProjectDiscussionStatus.DRAFT_COMMENT) &&
          newReply[DiscussionFormKeys.FURTHER_ACTION] &&
          newReply[DiscussionFormKeys.FURTHER_ACTION].trim() !== '',
      )
      .map((newReply) => convertFormValueToReplyRequest(newReply));

    if (discussionRequests.length > 0) mutateUpdateProjectDiscussionsAsDraft(discussionRequests);
    if (replyRequests.length > 0) mutateUpdateProjectDiscussionRepliesAsDraft(replyRequests);
  };

  const onIssueSelected = (data: any) => {
    const newItems = (data[DiscussionFormKeys.FORM] as any) || {};
    const discussionRequests = Object.keys(newItems)
      .filter(
        (key) =>
          (selectedCount <= 0 || selectedDiscussions.includes(Number(key))) &&
          newItems[key][DiscussionFormKeys.STATUS] === EProjectDiscussionStatus.DRAFT_COMMENT,
      )
      .map((key) => convertFormValueToUpdateRequest(newItems[key]));

    const replyRequests = Object.keys(newItems)
      .filter(
        (key) =>
          (selectedCount <= 0 || selectedReplies.includes(Number(key))) &&
          (newItems[key][DiscussionFormKeys.STATUS] === EProjectDiscussionStatus.COMMENTED ||
            newItems[key][DiscussionFormKeys.STATUS] === EProjectDiscussionStatus.DRAFT_COMMENT),
      )
      .map((key) => convertFormValueToReplyRequest(newItems[key]));

    if (discussionRequests.length > 0) mutateUpdateProjectDiscussionsAsIssue(discussionRequests);
    if (replyRequests.length > 0) mutateUpdateProjectDiscussionRepliesAsIssue(replyRequests);
  };

  const topBarLeftItems = React.useMemo(() => {
    const generateIssueToButton = (toType: string | undefined) => {
      const toString = toType ? ` to ${toType}` : '';

      return {
        key: `Issue${toString}`,
        title: selectedCount ? `Issue selected (${selectedCount})${toString}` : `Issue all${toString}`,
        buttonIconName: 'EnvelopeCheck' as IconName,
        type: EDetailPageTopBarItemType.loadingButton,
        componentType: ComponentType.Success,
        isError: isErrorUpdateProjectDiscussionsAsIssue || isErrorUpdateProjectDiscussionRepliesAsIssue,
        isLoading: isLoadingUpdateProjectDiscussionsAsIssue || isLoadingUpdateProjectDiscussionRepliesAsIssue,
        isSuccess: isSuccessUpdateProjectDiscussionsAsIssue || isSuccessUpdateProjectDiscussionRepliesAsIssue,
        onClick: () => {
          setSelectedIssueToType(toType || undefined);
          useFormMethods.clearErrors();
          useFormMethods.handleSubmit(onIssueSelected)();
        },
        disable: isUploading || isAnyActionLoading,
      };
    };

    const commentButtons = localCanComment
      ? [
          {
            key: 'Add New Discussion',
            title: 'Add New Discussion',
            buttonIconName: 'FileEarmarkPlus' as IconName,
            type: EDetailPageTopBarItemType.loadingButton,
            componentType: ComponentType.Primary,
            isError: isErrorCreateProjectDiscussion,
            isLoading: isLoadingCreateProjectDiscussion,
            isSuccess: isSuccessCreateProjectDiscussion,
            onClick: () => {
              mutateCreateProjectDiscussion({ projectId, ...projectDiscussionFormProps });
            },
            disable: isUploading || isAnyActionLoading,
          },
        ]
      : [];

    const commentOrReplyButton =
      (localCanComment &&
        discussions &&
        discussions.some((d) => d.status === EProjectDiscussionStatus.DRAFT_COMMENT)) ||
      (discussions &&
        discussions.some(
          (d) =>
            localCanResponse(d) &&
            (d.status === EProjectDiscussionStatus.DRAFT_RESPONSE || d.status === EProjectDiscussionStatus.COMMENTED),
        ))
        ? [
            {
              key: 'Save All Drafts',
              title: 'Save All Drafts',
              buttonIconName: 'Check2All' as IconName,
              type: EDetailPageTopBarItemType.loadingButton,
              componentType: ComponentType.Secondary,
              isError: isErrorUpdateProjectDiscussionsAsDraft || isErrorUpdateProjectDiscussionRepliesAsDraft,
              isLoading: isLoadingUpdateProjectDiscussionsAsDraft || isLoadingUpdateProjectDiscussionRepliesAsDraft,
              isSuccess: isSuccessUpdateProjectDiscussionsAsDraft || isSuccessUpdateProjectDiscussionRepliesAsDraft,
              onClick: () => {
                useFormMethods.clearErrors();
                useFormMethods.handleSubmit(onSaveAllDrafts)();
              },
              disable: isUploading || isAnyActionLoading,
            },
            ...(issueToTypes && issueToTypes.length > 0
              ? issueToTypes.map((issueToType) => generateIssueToButton(issueToType))
              : [generateIssueToButton(undefined)]),
          ]
        : [];
    const titleText = title ? [<b key="discussion-list-top-left-bar-title">{title}</b>] : [];

    return [...titleText, ...commentButtons, ...commentOrReplyButton];
  }, [
    JSON.stringify(projectDiscussionFormProps),
    isErrorCreateProjectDiscussion,
    isLoadingCreateProjectDiscussion,
    isSuccessCreateProjectDiscussion,
    isErrorUpdateProjectDiscussionsAsDraft,
    isErrorUpdateProjectDiscussionRepliesAsDraft,
    isLoadingUpdateProjectDiscussionsAsDraft,
    isLoadingUpdateProjectDiscussionRepliesAsDraft,
    isSuccessUpdateProjectDiscussionsAsDraft,
    isSuccessUpdateProjectDiscussionRepliesAsDraft,
    isErrorUpdateProjectDiscussionsAsIssue,
    isErrorUpdateProjectDiscussionRepliesAsIssue,
    isLoadingUpdateProjectDiscussionsAsIssue,
    isLoadingUpdateProjectDiscussionRepliesAsIssue,
    isSuccessUpdateProjectDiscussionsAsIssue,
    isSuccessUpdateProjectDiscussionRepliesAsIssue,
    isUploading,
    selectedCount,
    localCanComment,
    localCanResponse,
    title,
  ]);

  const topBarRightItems = React.useMemo(() => {
    if (canPrint) {
      let exportFunction = null;
      let formId = null;

      switch (formType) {
        case EProjectDiscussionFormType.DESIGN_BRIEF:
          exportFunction = downloadDesignBriefProjectDiscussionsPdfAsync;
          formId = projectDesignBriefId;
          break;
        case EProjectDiscussionFormType.DESIGN_MODIFICATION:
          exportFunction = downloadDesignModificationDesignerProjectDiscussionsPdfAsync;
          formId = projectDesignModificationId;
          break;
        case EProjectDiscussionFormType.DESIGN_MODIFICATION_ICE:
          exportFunction = downloadDesignModificationCheckerProjectDiscussionsPdfAsync;
          formId = projectDesignModificationId;
          break;
        case EProjectDiscussionFormType.LOADING_REMOVAL_CERT:
          exportFunction = downloadLoadingRemovalCertProjectDiscussionsPdfAsync;
          formId = projectLoadingRemovalCertId;
          break;
        case EProjectDiscussionFormType.TRANSMITTAL:
          exportFunction = downloadTransmittalProjectDiscussionsPdfAsync;
          formId = projectTransmittalId;
          break;
        default:
          break;
      }
      if (exportFunction && formId)
        return [
          <DownloadButton
            key="printPdf"
            exportFunction={exportFunction as (props: IPdfFileDownloadRequest) => Promise<IFileResponse>}
            defaultExportProps={{
              projectId,
              id: formId as number,
            }}
            buttonRender={(exportFile) => (
              <Button prefixIconName="Printer" type={ComponentType.Secondary} onClick={() => exportFile()}>
                Print
              </Button>
            )}
          />,
        ];
    }
    return [];
  }, [formType, projectDesignBriefId, projectTransmittalId, canPrint]);

  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });

  React.useEffect(() => {
    const currentFormValues: any = useFormMethods.getValues()[DiscussionFormKeys.FORM] ?? {};
    const newFormValues: any = {};

    (discussions || []).forEach((item) => {
      const currentFormValue = currentFormValues[item.id];
      newFormValues[item.id] = {
        [DiscussionFormKeys.COMMENT]: currentFormValue?.comment ?? item.comment ?? '',
        [DiscussionFormKeys.FURTHER_ACTION]: currentFormValue?.furtherAction ?? item.furtherAction ?? '',
        [DiscussionFormKeys.ID]: item.id,
        [DiscussionFormKeys.ROW_VERSION]: item.rowVersion,
        [DiscussionFormKeys.STATUS]: item.status,
        [DiscussionFormKeys.IS_ISSUE_COMMENT]: currentFormValue?.isIssueComment ?? false,
        [DiscussionFormKeys.IS_ISSUE_REPLY]: currentFormValue?.isIssueReply ?? false,
      };
    });
    useFormMethods.setValue(`${DiscussionFormKeys.FORM}`, newFormValues);
  }, [JSON.stringify(discussions)]);

  React.useEffect(() => {
    useFormMethods.setValue('canComment', localCanComment);
    useFormMethods.setValue('canResponse', localCanResponse);
  }, [localCanComment, localCanResponse]);

  return (
    <ContentLoader
      active={isLoadingDiscussions}
      linePadding={8}
      paragraphConfigs={[
        { suffixBlankRow: true, numberOfRows: 5, lineHeight: 10 },
        { suffixBlankRow: true, numberOfRows: 5, lineHeight: 10 },
        { suffixBlankRow: true, numberOfRows: 5, lineHeight: 10 },
        { suffixBlankRow: true, numberOfRows: 5, lineHeight: 10 },
      ]}
    >
      <>
        <ApiSuccessToast
          successes={[
            {
              isSuccess: isSuccessCreateProjectDiscussion,
              message: 'Create discussion success',
              reset: resetCreateProjectDiscussion,
            },
            {
              isSuccess: isSuccessUpdateProjectDiscussionsAsDraft,
              message: 'Save draft discussions success',
              reset: resetUpdateProjectDiscussionsAsDraft,
            },
            {
              isSuccess: isSuccessUpdateProjectDiscussionsAsIssue,
              message: 'Issue discussions success',
              reset: resetUpdateProjectDiscussionsAsIssue,
            },
            {
              isSuccess: isSuccessUpdateProjectDiscussionAsDraft,
              message: 'Save draft discussion success',
              reset: resetUpdateProjectDiscussionAsDraft,
            },
            {
              isSuccess: isSuccessUpdateProjectDiscussionAsIssue,
              message: 'Issue discussion success',
              reset: resetUpdateProjectDiscussionAsIssue,
            },
            {
              isSuccess: isSuccessUpdateProjectDiscussionRepliesAsDraft,
              message: 'Save draft replies success',
              reset: resetUpdateProjectDiscussionRepliesAsDraft,
            },
            {
              isSuccess: isSuccessUpdateProjectDiscussionRepliesAsIssue,
              message: 'Issue replies success',
              reset: resetUpdateProjectDiscussionRepliesAsIssue,
            },
            {
              isSuccess: isSuccessUpdateProjectDiscussionReplyAsDraft,
              message: 'Save draft reply success',
              reset: resetUpdateProjectDiscussionReplyAsDraft,
            },
            {
              isSuccess: isSuccessUpdateProjectDiscussionReplyAsIssue,
              message: 'Issue reply success',
              reset: resetUpdateProjectDiscussionReplyAsIssue,
            },
            {
              isSuccess: isSuccessDeleteProjectDiscussion,
              message: 'Delete discussion success',
              reset: resetDeleteProjectDiscussion,
            },
          ]}
          callbackAfterToasted={() => {
            queryClient.invalidateQueries([
              projectId,
              { projectId, ...projectDiscussionFormProps },
              FETCH_QUERIES_NAME.PROJECT_DISCUSSION_LIST,
            ]);
          }}
        />
        <ApiErrorDisplay
          setFormError={(
            name: string,
            error: ErrorOption,
            options?:
              | {
                  shouldFocus: boolean;
                }
              | undefined,
          ) => {
            useFormMethods.setError(`${DiscussionFormKeys.FORM}${name}`, error, options);
          }}
          errors={[
            { error: errorDiscussions, genericError: 'Get discussions failed' },
            {
              error: errorCreateProjectDiscussion,
              genericError: 'Create discussion failed',
              reset: resetCreateProjectDiscussion,
            },
            {
              error: errorUpdateProjectDiscussionsAsDraft,
              genericError: 'Save draft discussions failed',
              reset: resetUpdateProjectDiscussionsAsDraft,
            },
            {
              error: errorUpdateProjectDiscussionsAsIssue,
              genericError: 'Issue discussions failed',
              reset: resetUpdateProjectDiscussionsAsIssue,
            },
            {
              error: errorUpdateProjectDiscussionAsDraft,
              genericError: 'Save draft discussion failed',
              reset: resetUpdateProjectDiscussionAsDraft,
            },
            {
              error: errorUpdateProjectDiscussionAsIssue,
              genericError: 'Issue discussion failed',
              reset: resetUpdateProjectDiscussionAsIssue,
            },
            {
              error: errorUpdateProjectDiscussionRepliesAsDraft,
              genericError: 'Save draft replies failed',
              reset: resetUpdateProjectDiscussionRepliesAsDraft,
            },
            {
              error: errorUpdateProjectDiscussionRepliesAsIssue,
              genericError: 'Issue replies failed',
              reset: resetUpdateProjectDiscussionRepliesAsIssue,
            },
            {
              error: errorUpdateProjectDiscussionReplyAsDraft,
              genericError: 'Save draft reply failed',
              reset: resetUpdateProjectDiscussionReplyAsDraft,
            },
            {
              error: errorUpdateProjectDiscussionReplyAsIssue,
              genericError: 'Issue reply failed',
              reset: resetUpdateProjectDiscussionReplyAsIssue,
            },
            {
              error: errorDeleteProjectDiscussion,
              genericError: 'Delete discussion failed',
              reset: resetDeleteProjectDiscussion,
            },
          ]}
        />

        {(!discussions || discussions.length <= 0) && showNoneWhenNoRecordInReadOnly ? undefined : (
          <DetailPageTopBar leftItems={topBarLeftItems} rightItems={topBarRightItems} />
        )}
        {(!discussions || discussions.length <= 0) && !showNoneWhenNoRecordInReadOnly ? <RecordsNotFound /> : undefined}
        {discussions && discussions.length > 0 && (
          <Form
            useFormMethods={useFormMethods}
            render={(formFieldMethods) => {
              return (
                <>
                  {discussions.map((discussion, index) => {
                    return (
                      <DiscussionRow
                        isAnyActionLoading={isAnyActionLoading}
                        isFileUploading={isUploading}
                        onFileUploading={(uuid, isFileUploading) => {
                          setUploadStatusMap((old) => {
                            return { ...old, [uuid]: isFileUploading };
                          });
                        }}
                        canViewDraftComment={canViewDraftComment}
                        canComment={localCanComment}
                        canResponse={localCanResponse}
                        useFetchFilesQueryKey={[
                          projectId,
                          { projectId, ...projectDiscussionFormProps },
                          FETCH_QUERIES_NAME.PROJECT_DISCUSSION_LIST,
                        ]}
                        key={`gcl-discussion-row-${discussion.id}`}
                        issueToTypes={issueToTypes}
                        fileFormType={fileFormType}
                        discussionFileType={discussionFileType}
                        discussion={discussion}
                        updateDiscussionAsDraft={(id: number) => {
                          const updateFn = onGenerateUpdateDiscussionFn(id, undefined, true);
                          useFormMethods.clearErrors();
                          useFormMethods.handleSubmit(updateFn)();
                        }}
                        updateDiscussionAsIssue={(id: number, issueToType: string | undefined) => {
                          const updateFn = onGenerateUpdateDiscussionFn(id, issueToType, false);
                          useFormMethods.clearErrors();
                          useFormMethods.handleSubmit(updateFn)();
                        }}
                        updateReplyAsDraft={(id: number) => {
                          const updateFn = onGenerateUpdateReplyFn(id, true);
                          useFormMethods.clearErrors();
                          useFormMethods.handleSubmit(updateFn)();
                        }}
                        updateReplyAsIssue={(id: number, issueToType: string | undefined) => {
                          const updateFn = onGenerateUpdateReplyFn(id, false);
                          useFormMethods.clearErrors();
                          useFormMethods.handleSubmit(updateFn)();
                        }}
                        formFieldMethods={useFormMethods}
                        onSelectDiscussion={onSelectDiscussion}
                        onSelectReply={onSelectReply}
                        onDeleteDiscussion={onDeleteDiscussion}
                        isLoadingDeleteDiscussion={isLoadingDeleteProjectDiscussion}
                        isErrorDeleteDiscussion={isErrorDeleteProjectDiscussion}
                        isSuccessDeleteDiscussion={isSuccessDeleteProjectDiscussion}
                        isLoadingUpdateAsDraft={
                          isLoadingUpdateProjectDiscussionAsDraft || isLoadingUpdateProjectDiscussionReplyAsDraft
                        }
                        isErrorUpdateAsDraft={
                          isErrorUpdateProjectDiscussionAsDraft || isErrorUpdateProjectDiscussionReplyAsDraft
                        }
                        isSuccessUpdateAsDraft={
                          isSuccessUpdateProjectDiscussionAsDraft || isSuccessUpdateProjectDiscussionReplyAsDraft
                        }
                        isLoadingUpdateAsIssue={
                          isLoadingUpdateProjectDiscussionAsIssue || isLoadingUpdateProjectDiscussionReplyAsIssue
                        }
                        isErrorUpdateAsIssue={
                          isErrorUpdateProjectDiscussionAsIssue || isErrorUpdateProjectDiscussionReplyAsIssue
                        }
                        isSuccessUpdateAsIssue={
                          isSuccessUpdateProjectDiscussionAsIssue || isSuccessUpdateProjectDiscussionReplyAsIssue
                        }
                      />
                    );
                  })}
                </>
              );
            }}
          />
        )}
      </>
    </ContentLoader>
  );
};
